import React from 'react';
import { 
  MapPin, 
  Navigation, 
  Clock, 
  CheckCircle2, 
  Loader2,
  Car,
  User,
  Phone
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

// Status configuration
const STATUS_CONFIG = {
  searching: {
    label: 'Buscando conductor',
    color: 'bg-blue-500',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    icon: Loader2,
    animate: true
  },
  assigned: {
    label: 'Conductor asignado',
    color: 'bg-teal-500',
    textColor: 'text-teal-600',
    bgColor: 'bg-teal-50',
    icon: CheckCircle2
  },
  en_route: {
    label: 'En camino a recogerte',
    color: 'bg-[#00BFA6]',
    textColor: 'text-[#00BFA6]',
    bgColor: 'bg-[#00BFA6]/10',
    icon: Navigation
  },
  arrived: {
    label: 'Conductor ha llegado',
    color: 'bg-amber-500',
    textColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    icon: MapPin
  },
  in_progress: {
    label: 'Viaje en curso',
    color: 'bg-green-500',
    textColor: 'text-green-600',
    bgColor: 'bg-green-50',
    icon: Car
  },
  completed: {
    label: 'Servicio completado',
    color: 'bg-slate-500',
    textColor: 'text-slate-600',
    bgColor: 'bg-slate-50',
    icon: CheckCircle2
  }
};

// Status Badge
export const TrackingStatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.searching;
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bgColor}`}>
      <Icon className={`w-4 h-4 ${config.textColor} ${config.animate ? 'animate-spin' : ''}`} />
      <span className={`text-sm font-medium ${config.textColor}`}>{config.label}</span>
    </div>
  );
};

// ETA Display
export const ETADisplay = ({ minutes, className = '' }) => {
  if (minutes === null || minutes === undefined) return null;
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Clock className="w-5 h-5 text-[#00BFA6]" />
      <span className="text-lg font-bold text-slate-800">
        {minutes === 0 ? 'Llegando' : `${minutes} min`}
      </span>
    </div>
  );
};

// Connection Status Indicator
export const ConnectionStatus = ({ isConnected }) => (
  <div className="flex items-center gap-2 text-sm">
    <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
    <span className={isConnected ? 'text-green-600' : 'text-red-600'}>
      {isConnected ? 'En vivo' : 'Desconectado'}
    </span>
  </div>
);

// Full Tracking Card
export const TrackingCard = ({ 
  status, 
  eta, 
  driverName, 
  driverPhone,
  vehicleInfo,
  isConnected = false,
  onCallDriver,
  onCancel
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.searching;
  const Icon = config.icon;
  
  return (
    <Card className="card-elevated overflow-hidden">
      {/* Status Header */}
      <div className={`${config.color} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Icon className={`w-5 h-5 ${config.animate ? 'animate-spin' : ''}`} />
            <span className="font-semibold">{config.label}</span>
          </div>
          <ConnectionStatus isConnected={isConnected} />
        </div>
      </div>
      
      <CardContent className="p-4 space-y-4">
        {/* ETA */}
        {eta !== null && eta !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Tiempo estimado</span>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00BFA6]" />
              <span className="text-2xl font-bold text-slate-800">
                {eta === 0 ? 'Llegando' : `${eta} min`}
              </span>
            </div>
          </div>
        )}
        
        {/* Driver Info */}
        {driverName && (
          <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-[#00BFA6]/10 flex items-center justify-center">
              <User className="w-6 h-6 text-[#00BFA6]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-800">{driverName}</p>
              {vehicleInfo && (
                <p className="text-sm text-slate-500">{vehicleInfo}</p>
              )}
            </div>
            {driverPhone && (
              <Button 
                size="icon" 
                variant="ghost"
                onClick={onCallDriver}
                className="text-[#00BFA6] hover:bg-[#00BFA6]/10"
              >
                <Phone className="w-5 h-5" />
              </Button>
            )}
          </div>
        )}
        
        {/* Actions */}
        {status !== 'completed' && onCancel && (
          <Button 
            variant="outline" 
            className="w-full border-red-200 text-red-600 hover:bg-red-50"
            onClick={onCancel}
          >
            Cancelar servicio
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Mini tracking indicator (for map overlay)
export const TrackingIndicator = ({ status, eta, isConnected }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.searching;
  const Icon = config.icon;
  
  return (
    <div className="absolute top-4 left-4 right-4 z-10">
      <div className={`${config.bgColor} backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-white/20`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${config.color} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 text-white ${config.animate ? 'animate-spin' : ''}`} />
            </div>
            <div>
              <p className={`font-semibold ${config.textColor}`}>{config.label}</p>
              {eta !== null && eta !== undefined && (
                <p className="text-sm text-slate-600">
                  {eta === 0 ? 'Llegando ahora' : `Llega en ${eta} min`}
                </p>
              )}
            </div>
          </div>
          <ConnectionStatus isConnected={isConnected} />
        </div>
      </div>
    </div>
  );
};

export default TrackingCard;
